import React, { useState, useEffect } from "react";
import { X, CreditCard, Lock } from "lucide-react";
import ParagraphTag from "../reusebale/Paragraph";
import MyLoader from "../reusebale/myLoader";
import { ShowError } from "../../utils/ToasFunction";

const AddCardModal = ({ onClose, onCardAdded, isLoading }) => {
  const [cardForm, setCardForm] = useState({
    holderName: "",
    cardNumber: "",
    cvv: "",
    brand: "",
    expiryDate: "", // MM/YY format
    addressLine1: "",
    city: "",
    state: "",
    zip: "",
  });

  const [detectedBrand, setDetectedBrand] = useState(null);

  // Card brand detection based on first digits
  const detectCardBrand = (cardNumber) => {
    const digits = cardNumber.replace(/\s/g, "");
    
    // Visa: starts with 4
    if (/^4/.test(digits)) {
      return "VISA";
    }
    // Mastercard: starts with 51-55 or 2221-2720
    if (/^5[1-5]/.test(digits) || /^2(22[1-9]|2[3-9]|[3-6]|7[01]|720)/.test(digits)) {
      return "MASTERCARD";
    }
    // American Express: starts with 34 or 37
    if (/^3[47]/.test(digits)) {
      return "AMEX";
    }
    // Discover: starts with 6011, 622126-622925, 644-649, or 65
    if (/^6011/.test(digits) || /^64[4-9]/.test(digits) || /^65/.test(digits)) {
      return "DISCOVER";
    }
    
    return null;
  };

  // Auto-detect brand when card number changes
  useEffect(() => {
    if (cardForm.cardNumber.length >= 2) {
      const brand = detectCardBrand(cardForm.cardNumber);
      setDetectedBrand(brand);
      if (brand) {
        setCardForm(prev => ({ ...prev, brand }));
      }
    } else {
      setDetectedBrand(null);
    }
  }, [cardForm.cardNumber]);

  const handleInputChange = (field, value) => {
    setCardForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCardNumberChange = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, "");
    // Limit to 16 digits
    if (digits.length <= 16) {
      // Format with spaces every 4 digits
      const formatted = digits.match(/.{1,4}/g)?.join(" ") || digits;
      handleInputChange("cardNumber", formatted);
    }
  };

  const handleExpiryDateChange = (value) => {
    // Remove all non-digit characters
    let digits = value.replace(/\D/g, "");
    
    // Auto-add slash after 2 digits
    if (digits.length >= 2) {
      digits = digits.slice(0, 2) + "/" + digits.slice(2, 4);
    }
    
    // Limit to MM/YY format
    if (digits.length <= 5) {
      handleInputChange("expiryDate", digits);
    }
  };

  const handleCVVChange = (value) => {
    // Remove all non-digit characters and limit to 4 digits
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 4) {
      handleInputChange("cvv", digits);
    }
  };

  const handleZipChange = (value) => {
    // Remove all non-digit characters and limit to 10 digits
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 10) {
      handleInputChange("zip", digits);
    }
  };

  const validateForm = () => {
    const cardDigits = cardForm.cardNumber.replace(/\s/g, "");
    
    if (!cardForm.cardNumber.trim()) {
      ShowError("Card number is required");
      return false;
    }
    if (cardDigits.length < 13 || cardDigits.length > 16) {
      ShowError("Invalid card number");
      return false;
    }
    if (!detectedBrand) {
      ShowError("Unable to detect card type. Please check card number");
      return false;
    }
    if (!cardForm.holderName.trim()) {
      ShowError("Cardholder name is required");
      return false;
    }
    
    // Validate expiry date format
    const expiryParts = cardForm.expiryDate.split("/");
    if (expiryParts.length !== 2) {
      ShowError("Expiry date must be in MM/YY format");
      return false;
    }
    const month = parseInt(expiryParts[0], 10);
    const year = parseInt(expiryParts[1], 10);
    if (month < 1 || month > 12) {
      ShowError("Invalid expiry month");
      return false;
    }
    if (year < 0 || expiryParts[1].length !== 2) {
      ShowError("Invalid expiry year");
      return false;
    }
    
    if (cardForm.cvv.length < 3 || cardForm.cvv.length > 4) {
      ShowError("CVV must be 3 or 4 digits");
      return false;
    }
    if (!cardForm.addressLine1.trim()) {
      ShowError("Billing address is required");
      return false;
    }
    if (!cardForm.city.trim()) {
      ShowError("City is required");
      return false;
    }
    if (!cardForm.state.trim()) {
      ShowError("State is required");
      return false;
    }
    if (!cardForm.zip.trim()) {
      ShowError("ZIP code is required");
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Parse expiry date
      const [month, year] = cardForm.expiryDate.split("/");
      const fullYear = "20" + year; // Convert YY to YYYY
      
      const cardData = {
        card: [
          {
            holderName: cardForm.holderName,
            cardNumber: cardForm.cardNumber.replace(/\s/g, ""), // Remove spaces
            cvv: cardForm.cvv,
            brand: cardForm.brand,
            expiryMonth: month,
            expiryYear: fullYear,
            billingAddress: {
              addressLine1: cardForm.addressLine1,
              city: cardForm.city,
              state: cardForm.state,
              zip: cardForm.zip,
            },
          },
        ],
      };
      
      onCardAdded(cardData);
    }
  };

  // Card brand icon component
  const CardBrandIcon = ({ brand }) => {
    if (brand === "VISA") {
      return (
        <svg className="w-12 h-8" viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#1A1F71"/>
          <text x="24" y="20" fontSize="12" fontWeight="bold" fill="white" textAnchor="middle">VISA</text>
        </svg>
      );
    }
    if (brand === "MASTERCARD") {
      return (
        <svg className="w-12 h-8" viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#EB001B"/>
          <circle cx="18" cy="16" r="8" fill="#FF5F00" opacity="0.8"/>
          <circle cx="30" cy="16" r="8" fill="#F79E1B" opacity="0.8"/>
        </svg>
      );
    }
    if (brand === "AMEX") {
      return (
        <svg className="w-12 h-8" viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#006FCF"/>
          <text x="24" y="20" fontSize="10" fontWeight="bold" fill="white" textAnchor="middle">AMEX</text>
        </svg>
      );
    }
    if (brand === "DISCOVER") {
      return (
        <svg className="w-12 h-8" viewBox="0 0 48 32" fill="none">
          <rect width="48" height="32" rx="4" fill="#FF6000"/>
          <text x="24" y="20" fontSize="9" fontWeight="bold" fill="white" textAnchor="middle">DISCOVER</text>
        </svg>
      );
    }
    return <CreditCard className="w-6 h-6 text-gray-400" />;
  };

  return (
    <main className="w-full h-screen backdrop-blur-sm bg-black/80 fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-4">
      <section className="w-[90%] sm:w-[500px] bg-white rounded-2xl mx-auto my-4 shadow-2xl">
        <div className="bg-white py-8 px-8 rounded-2xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-darkColor font-popinsBold text-2xl">
              Add Payment Method
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isLoading}
            >
              <X size={24} />
            </button>
          </div>

          <p className="text-gray-600 text-sm mb-6 font-popinsRegular">
            Enter your card details to add a new payment method
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Card Number */}
            <div>
              <label className="block text-sm font-popinsMedium text-gray-700 mb-2">
                Card Number
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <CardBrandIcon brand={detectedBrand} />
                </div>
                <input
                  type="text"
                  value={cardForm.cardNumber}
                  onChange={(e) => handleCardNumberChange(e.target.value)}
                  placeholder="1234 5678 9012 3456"
                  className="w-full pl-20 pr-4 py-3 text-base font-popinsRegular border border-gray-300 rounded-lg text-darkColor placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-themeColor focus:border-transparent"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Cardholder Name */}
            <div>
              <label className="block text-sm font-popinsMedium text-gray-700 mb-2">
                Cardholder Name
              </label>
              <input
                type="text"
                value={cardForm.holderName}
                onChange={(e) => handleInputChange("holderName", e.target.value)}
                placeholder="John Doe"
                className="w-full px-4 py-3 text-base font-popinsRegular border border-gray-300 rounded-lg text-darkColor placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-themeColor focus:border-transparent"
                disabled={isLoading}
              />
            </div>

            {/* Expiry Date and CVV */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-popinsMedium text-gray-700 mb-2">
                  Expiry Date
                </label>
                <input
                  type="text"
                  value={cardForm.expiryDate}
                  onChange={(e) => handleExpiryDateChange(e.target.value)}
                  placeholder="MM/YY"
                  className="w-full px-4 py-3 text-base font-popinsRegular border border-gray-300 rounded-lg text-darkColor placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-themeColor focus:border-transparent"
                  disabled={isLoading}
                  maxLength={5}
                />
              </div>
              <div>
                <label className="block text-sm font-popinsMedium text-gray-700 mb-2">
                  CVV
                </label>
                <input
                  type="text"
                  value={cardForm.cvv}
                  onChange={(e) => handleCVVChange(e.target.value)}
                  placeholder="123"
                  className="w-full px-4 py-3 text-base font-popinsRegular border border-gray-300 rounded-lg text-darkColor placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-themeColor focus:border-transparent"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Billing Address Section */}
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-base font-popinsSemiBold text-gray-700 mb-4">
                Billing Address
              </h3>
              
              {/* Address Line 1 */}
              <div className="mb-4">
                <label className="block text-sm font-popinsMedium text-gray-700 mb-2">
                  Address Line 1
                </label>
                <input
                  type="text"
                  value={cardForm.addressLine1}
                  onChange={(e) => handleInputChange("addressLine1", e.target.value)}
                  placeholder="123 Main St"
                  className="w-full px-4 py-3 text-base font-popinsRegular border border-gray-300 rounded-lg text-darkColor placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-themeColor focus:border-transparent"
                  disabled={isLoading}
                />
              </div>

              {/* City and State */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-popinsMedium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={cardForm.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                    placeholder="New York"
                    className="w-full px-4 py-3 text-base font-popinsRegular border border-gray-300 rounded-lg text-darkColor placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-themeColor focus:border-transparent"
                    disabled={isLoading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-popinsMedium text-gray-700 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    value={cardForm.state}
                    onChange={(e) => handleInputChange("state", e.target.value)}
                    placeholder="NY"
                    className="w-full px-4 py-3 text-base font-popinsRegular border border-gray-300 rounded-lg text-darkColor placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-themeColor focus:border-transparent"
                    disabled={isLoading}
                    maxLength={2}
                  />
                </div>
              </div>

              {/* ZIP Code */}
              <div>
                <label className="block text-sm font-popinsMedium text-gray-700 mb-2">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={cardForm.zip}
                  onChange={(e) => handleZipChange(e.target.value)}
                  placeholder="10001"
                  className="w-full px-4 py-3 text-base font-popinsRegular border border-gray-300 rounded-lg text-darkColor placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-themeColor focus:border-transparent"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Security Message */}
            <div className="flex items-center gap-2 bg-gray-50 px-4 py-3 rounded-lg">
              <Lock className="w-4 h-4 text-gray-500" />
              <span className="text-xs text-gray-600 font-popinsRegular">
                Your payment information is encrypted and secure
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-popinsSemiBold hover:bg-gray-50 transition-colors text-base"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-6 py-3 bg-black text-white rounded-lg font-popinsSemiBold hover:bg-gray-800 transition-colors text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <MyLoader />
                    <span className="ml-2">Adding...</span>
                  </>
                ) : (
                  "Add Card"
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
};

export default AddCardModal;
